import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Modal, Button } from "react-bootstrap";
import Breadcrumbs from "../../../../shared/components/Breadcrumbs";
import ServerSideGrid from "../../../../shared/components/listview/ServerSideGrid";
import DefaultButton from "../../../../shared/components/DefaultButton";
import { ARTICLES, EXPORT } from "../../../../shared/constants/Constants";
import { loadCategoryArticleListDataRequest } from "../redux/Actions";
import CategoryKB from "./CategoryKB";
const DEFAULT_COL_DEF = { filter: "agTextColumnFilter", sortable: true, resizable: true, flex: 1, minWidth: 150 };

const ArticlesListTable = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [reload, setReload] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [exportEnabled, setExportEnabled] = useState(false);

  const { categoryArticleList } = useSelector((store) => store.articleList); 
  useEffect(() => {
    dispatch(loadCategoryArticleListDataRequest());
  }, [dispatch, reload]);

  const handleOpenArticleModal = useCallback((row) => {
    setSelectedCategory(row);
    setShowArticleModal(true);
  }, []);

  const handleArticleSuccess = () => {
    setShowArticleModal(false);
    setReload((prev) => !prev);
  };

  const handleClearFilters = useCallback(() => {
    gridRef.current?.getApi()?.setFilterModel(null);
  }, []);

  const handleQuestionsClick = useCallback((row) => {
    setSelectedQuestions(Array.isArray(row.questions) ? row.questions : []);
    setShowQuestionsModal(true);
  }, []);

  const handleSelectionChanged = useCallback((event) => {
    setExportEnabled(event.api.getSelectedNodes().length > 0);
  }, []);

  const handleExport = useCallback(() => {
    gridRef.current?.exportDataAsCsv({
      fileName: `${ARTICLES}_${new Date().toISOString().slice(0, 10)}`,
      onlySelected: true,
      columnKeys: ["category", "sub_category", "company_name", "ticketingportal", "question_count", "routing_display"],
    });
  }, []);

  const rowData = useMemo(
    () =>
      (categoryArticleList?.data ?? []).map((item) => ({
        ...item,
        ticketingportal: item.ticketingportal ? "YES" : "NO",
        question_count: Array.isArray(item.questions)
          ? `${item.questions.length} Question${item.questions.length !== 1 ? "s" : ""}`
          : "0 Questions",
        routing_display:
          item.isrouting && item.routing
            ? `Group: ${item.routing.adminGroupName || "-"}`
            : "Not Configured",
      })),
    [categoryArticleList?.data]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Add Article",
        field: "article_id",
        type: "customButton",
        width: 140,
        pinned: "left",
        dynamicLabel: (row) => (row.article_id ? "Edit Article" : "Add Article"),
        functionTo: (row) => handleOpenArticleModal(row),
      },
      { headerName: "Category", field: "category", pinned: "left" },
      { headerName: "Subcategory", field: "sub_category" },
      { headerName: "Company", field: "company_name" },
      { headerName: "Ticketing Portal", field: "ticketingportal", type: "customBadge", width: 150 },
      {
        headerName: "Questions",
        field: "question_count",
        type: "customLink",
        functionTo: (row) => handleQuestionsClick(row),
      },
      { headerName: "Routing Rules", field: "routing_display" },
    ],
    [handleOpenArticleModal, handleQuestionsClick]
  );

  return (
    <>
      <Row className="detailsPageHeader">
        <Col>
          <Breadcrumbs children={[{ href: "articles", label: ARTICLES }]} />
        </Col>
      </Row>

      <Row>
        <Card className="table-card">
          <Card.Body>
            <div className="mb-2">
              <Button
                style={{ width: 115 }}
                className="default-btn-color py-1"
                onClick={handleExport}
                disabled={!exportEnabled}
              >
                {EXPORT}
              </Button>
              <Button
                style={{ width: 115 }}
                className="default-btn-color mx-1 py-1"
                onClick={handleClearFilters}
              >
                Clear Filters
              </Button>
            </div>
            <ServerSideGrid
              ref={gridRef}
              clientSide={true}
              columnDefs={columnDefs}
              rowData={rowData}
              paginationPageSize={80}
              tableHeight="calc(100vh - 240px)"
              defaultColDef={DEFAULT_COL_DEF}
              onSelectionChanged={handleSelectionChanged}
            />
          </Card.Body>
        </Card>
      </Row>

     {/* Question Model */}
      <Modal show={showQuestionsModal} onHide={() => setShowQuestionsModal(false)} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Questions</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedQuestions.length > 0 ? (
            <div className="mt-3">
              {selectedQuestions.map((q, index) => (
                <div key={index} className="d-flex align-items-center border rounded p-2 mb-2 bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <strong>{index + 1}. {q.name || q.title || q.question}</strong>
                    <span className="badge" style={{ backgroundColor: "rgba(0, 123, 255, 0.15)", color: "#0d6efd", borderRadius: "12px", fontWeight: "600", padding: "5px 10px", fontSize: "13px" }}>
                      {q.answerType === "text" ? "Text" : q.answerType === "options" ? "Options" : q.answerType}
                    </span>
                    {q.required && (
                      <span className="badge" style={{ backgroundColor: "rgba(220, 53, 69, 0.15)", color: "#dc3545", borderRadius: "12px", fontWeight: "600", padding: "5px 10px", fontSize: "13px" }}>
                        Required
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>No questions found for this category.</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowQuestionsModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      <CategoryKB
        show={showArticleModal}
        onHide={() => setShowArticleModal(false)}
        onSuccess={handleArticleSuccess}
        initialData={{ category: selectedCategory }}
      />
    </>
  );
};

export default ArticlesListTable;